import React, { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { leadsPageTourSteps } from './tourSteps';

const TOUR_STORAGE_KEY = 'crm_tour_leads_completed';
const TOUR_AUTO_START_KEY = 'crm_tour_leads_auto_started';

interface LeadsTourProps {
  runTour: boolean;
  onTourEnd: () => void;
}

const LeadsTour: React.FC<LeadsTourProps> = ({ runTour, onTourEnd }) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    setRun(runTour);
    if (runTour) {
      setStepIndex(0);
    }
  }, [runTour]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action, index, type } = data;

    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status as any)) {
      // Tour completed or skipped
      setRun(false);
      setStepIndex(0);

      // Save to localStorage that tour is completed
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');

      // Notify parent component
      onTourEnd();
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // Update step index
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }
  };

  return (
    <Joyride
      steps={leadsPageTourSteps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      disableOverlayClose={true}
      disableScrolling={true}
      disableScrollParentFix={true}
      spotlightPadding={0}
      hideBackButton={false}
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: '#02338A',
          zIndex: 10000,
          overlayColor: 'rgba(0, 0, 0, 0.6)',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltip: {
          animation: 'fadeIn 0.5s cubic-bezier(0.4, 0.0, 0.2, 1)',
        },
        overlay: {
          transition: 'opacity 0.5s cubic-bezier(0.4, 0.0, 0.2, 1)',
        },
        spotlight: {
          transition: 'opacity 0.5s cubic-bezier(0.4, 0.0, 0.2, 1)',
        },
        buttonNext: {
          backgroundColor: '#02338A',
          fontSize: 14,
          padding: '8px 16px',
          transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
        },
        buttonBack: {
          color: '#02338A',
          fontSize: 14,
          marginRight: 8,
          transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
        },
        buttonSkip: {
          color: '#6B7280',
          fontSize: 14,
          transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour',
      }}
      floaterProps={{
        styles: {
          floater: {
            transition: 'opacity 0.5s cubic-bezier(0.4, 0.0, 0.2, 1)',
          },
        },
      }}
    />
  );
};

export default LeadsTour;

// Helper functions for tour management
export const isTourCompleted = (): boolean => {
  return localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
};

export const hasAutoStarted = (): boolean => {
  return localStorage.getItem(TOUR_AUTO_START_KEY) === 'true';
};

export const markAutoStarted = (): void => {
  localStorage.setItem(TOUR_AUTO_START_KEY, 'true');
};

export const resetTour = (): void => {
  localStorage.removeItem(TOUR_STORAGE_KEY);
  localStorage.removeItem(TOUR_AUTO_START_KEY);
};
